export const CLIENT_PAGE_SIZE = 20;

export type PaginationToken = number | 'gap-left' | 'gap-right';

export const pageCountOf = (total: number, pageSize = CLIENT_PAGE_SIZE): number =>
  Math.max(1, Math.ceil(Math.max(0, total) / pageSize));

export const clampPage = (page: number, total: number, pageSize = CLIENT_PAGE_SIZE): number => {
  const maxPage = pageCountOf(total, pageSize) - 1;
  return Math.min(Math.max(0, page), maxPage);
};

/**
 * First/last page plus a ±2 window around the active page. Once there are more
 * than eight pages, gaps replace the omitted ranges (handoff PART 6c).
 */
export const paginationTokens = (page: number, pageCount: number): PaginationToken[] => {
  if (pageCount <= 8) {
    return Array.from({ length: pageCount }, (_, index) => index);
  }

  const safePage = Math.min(Math.max(0, page), pageCount - 1);
  const lastPage = pageCount - 1;
  const low = Math.max(1, safePage - 2);
  const high = Math.min(lastPage - 1, safePage + 2);
  const tokens: PaginationToken[] = [0];

  if (low > 1) tokens.push('gap-left');
  for (let current = low; current <= high; current += 1) tokens.push(current);
  if (high < lastPage - 1) tokens.push('gap-right');
  tokens.push(lastPage);

  return tokens;
};

export type PaginatedResult<T> = {
  items: T[];
  page: number;
  pageCount: number;
  /** Zero-based inclusive offset. */
  start: number;
  /** Zero-based exclusive offset. */
  end: number;
};

export const paginateItems = <T>(items: T[], page: number, pageSize = CLIENT_PAGE_SIZE): PaginatedResult<T> => {
  const safePage = clampPage(page, items.length, pageSize);
  const start = safePage * pageSize;
  const end = Math.min(start + pageSize, items.length);
  return {
    items: items.slice(start, end),
    page: safePage,
    pageCount: pageCountOf(items.length, pageSize),
    start,
    end
  };
};
